export const beforeItems = [
  "Your renal nutrition references are scattered across different documents.",
  "You spend time searching for specific nutritional assessment concepts.",
  "Comparing dietary considerations across different kidney disease contexts feels disorganized.",
  "You want a clearer framework for reviewing renal nutrition cases.",
  "Your worksheets and documentation resources are difficult to locate.",
  "You need a structured educational reference collection you can revisit.",
];

export const afterItems = [
  "Six educational volumes organized around distinct professional topics.",
  "Structured references covering kidney function and renal physiology.",
  "Educational frameworks for reviewing nutritional status.",
  "Organized materials covering renal dietary considerations.",
  "Case-based learning exercises for reviewing clinical scenarios.",
  "Worksheets for structuring nutrition care planning discussions.",
  "Documentation templates for organizing educational case reviews.",
];

export const challenges = [
  {
    title: "Your renal nutrition references are scattered",
    body: [
      "You’ve collected information from textbooks, professional courses, research articles, and clinical educational materials.",
      "But when you want to revisit a specific topic, finding the right reference can take longer than expected.",
    ],
    approach: "Bring related renal nutrition educational topics together in one organized reference collection.",
  },
  {
    title: "You want a clearer framework for nutritional assessment",
    body: [
      "Evaluating nutritional status involves understanding multiple factors, including dietary intake, anthropometric information, relevant laboratory findings, medical history, and individual circumstances.",
      "You want a structured way to study these elements and understand how they relate to renal nutrition.",
    ],
    approach: "Explore organized nutritional assessment frameworks, educational worksheets, and reference materials focused on kidney-related nutrition considerations.",
  },
  {
    title: "You want to better understand renal dietary considerations",
    body: [
      "Protein, sodium, potassium, phosphorus, fluid intake, and energy requirements can all be relevant to renal nutrition, depending on the individual’s condition and clinical circumstances.",
      "You want to study these topics without constantly switching between disconnected educational resources.",
    ],
    approach: "Review structured educational materials covering nutrient-related concepts, dietary considerations, and the factors that influence individualized nutrition recommendations.",
  },
  {
    title: "You need a more organized approach to renal nutrition cases",
    body: [
      "Clinical nutrition cases can involve multiple considerations, including kidney function, nutritional status, dietary patterns, comorbidities, and relevant clinical findings.",
      "You want a systematic way to organize the information you review.",
    ],
    approach: "Use educational case-analysis frameworks and nutrition care planning worksheets to structure your learning and professional discussions.",
  },
  {
    title: "You want to keep your nutrition documentation organized",
    body: [
      "Assessment records, dietary histories, nutrition care notes, monitoring information, and follow-up documentation can involve multiple forms and information sources.",
      "You want these resources to be easier to locate and organize.",
    ],
    approach: "Access educational documentation templates, assessment worksheets, and follow-up tracking resources designed for adaptation to your professional setting.",
  },
  {
    title: "You want to organize your continuing professional education",
    body: [
      "Your understanding of renal nutrition develops as research, clinical recommendations, and professional guidance evolve.",
      "You want an educational reference collection that helps you revisit important concepts and organize your learning.",
    ],
    approach: "Maintain a structured educational library covering major areas of renal nutrition, from foundational physiology to case analysis and documentation.",
  },
];

export const featuredTestimonial = {
  name: "Gabriela Ortiz",
  role: "Registered Dietitian · Outpatient Renal Clinic",
  photo: "https://cdn.magicpatterns.com/patterns/generated-images/7f23c0d3-ca91-4ffa-9160-49ea69af6a26.jpg",
  quote:
    "I had years of notes, PDFs and course handouts saved in different folders. Having physiology, assessment, nutrients, cases and documentation templates in one place finally gave my study routine a clear structure. I now review a topic before case discussions in minutes instead of digging through files.",
};

export const testimonials = [
  {
    name: "Maria Rodríguez",
    role: "Clinical Dietitian",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/72f73210-0873-4ef1-b58b-91b03c19b388.jpg",
    title: "A clearer way to organize complex topics",
    body: "Renal nutrition involves interconnected subjects, from kidney physiology to dietary considerations and nutrition care planning. A structured educational collection can provide a practical way to organize these topics and revisit them as part of continuing professional development.",
  },
  {
    name: "Daniel Herrera",
    role: "Clinical Nutritionist",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/53d772d4-8a55-4002-ab9c-85815aec871f.jpg",
    title: "Educational references that are easier to navigate",
    body: "When educational materials are organized into dedicated volumes, it becomes easier to identify relevant topics, review terminology, and return to specific reference sections during study.",
  },
  {
    name: "Lucía Navarro",
    role: "Nutrition Student",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/0b76acea-f5d9-41cc-a438-6b6828035425.jpg",
    title: "A more organized approach to case review",
    body: "Case-based educational exercises can help professionals structure their review of nutritional information, identify relevant questions, and discuss clinical considerations in an organized manner.",
  },
  {
    name: "Elena Castillo",
    role: "Renal Dietitian",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/cbab7e09-0137-460b-a520-e193711dc83b.jpg",
    title: "Practical worksheets for professional study",
    body: "Assessment worksheets and documentation templates can help organize learning activities and provide a framework for reviewing information relevant to renal nutrition.",
  },
  {
    name: "Samuel Okafor",
    role: "Clinical Nutrition Practitioner",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/1745ee1c-8867-4072-9d0c-09941afa6459.jpg",
    title: "A comprehensive educational reference collection",
    body: "Bringing physiology, assessment, dietary considerations, case analysis, and documentation together creates a more cohesive structure for studying renal nutrition.",
  },
];

export const faqs = [
  {
    q: "How will I receive the materials?",
    a: [
      "RENAL NUTRITION CLINICAL BLUEPRINT™ is a digital educational collection.",
      "After checkout, your access details will be sent to the email address you provide. The checkout page explains how you’ll receive your purchase and access the included materials.",
    ],
  },
  {
    q: "What exactly is included?",
    a: [
      "Your purchase includes six specialized educational volumes and five complementary professional resources.",
      "The collection covers renal physiology, nutritional assessment, dietary considerations, nutrition care planning, clinical case analysis, and documentation.",
      "The bonuses include nutrient comparison references, assessment worksheets, CKD nutrition educational materials, hypothetical case studies, and documentation and monitoring tools.",
    ],
  },
  {
    q: "Is this a physical product?",
    a: ["No. RENAL NUTRITION CLINICAL BLUEPRINT™ is a digital educational collection. No physical books or printed materials are shipped."],
  },
  {
    q: "Who is this collection for?",
    a: [
      "It’s designed for nutrition professionals, dietitians, clinical nutrition practitioners, nutrition students, and other eligible learners who want to organize their study of renal nutrition.",
      "The materials should be used within your professional qualifications and appropriate scope of practice.",
    ],
  },
  {
    q: "Is this a complete clinical training course?",
    a: ["No. This is an educational reference collection, not a substitute for formal education, supervised clinical training, professional qualifications, or specialty certification."],
  },
  {
    q: "Does this collection provide individualized nutrition prescriptions?",
    a: ["No. The materials provide educational references, worksheets, and hypothetical case exercises. Individual nutrition recommendations must be based on appropriate assessment, current evidence, and qualified professional judgment."],
  },
  {
    q: "Can I use the materials in any country?",
    a: ["The educational concepts may be useful to learners in different countries. However, professional requirements, clinical guidelines, food composition data, and documentation regulations vary by location. You should adapt the materials to the standards applicable to your setting."],
  },
  {
    q: "Can I download the files?",
    a: ["The collection is offered digitally. Available download methods and access details are explained at checkout."],
  },
  {
    q: "Is this a recurring subscription?",
    a: ["No. The advertised price is a one-time payment of $27 USD, with no recurring subscription."],
  },
  {
    q: "What if I decide the collection isn’t right for me?",
    a: ["Please review the refund policy and eligibility conditions presented at checkout before completing your purchase."],
  },
  {
    q: "Does this collection guarantee better clinical outcomes?",
    a: ["No. The collection is designed to support educational organization and professional learning. It does not guarantee clinical outcomes or replace appropriate training, assessment, and evidence-based practice."],
  },
];
